'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { LeadCaptureForm } from '@/components/calculator/LeadCaptureForm';

// Each option carries a risk weight (higher = more risk) and a gap flag that
// surfaces a specific recommendation when chosen.
interface Option {
  text: string;
  risk: number; // 0 (good) .. 3 (worst)
  gap?: string; // recommendation triggered when this option is selected
}

interface Question {
  id: string;
  text: string;
  helpText?: string;
  options: Option[];
}

const QUESTIONS: Question[] = [
  {
    id: 'policy',
    text: 'Do you have an acceptable-use policy for AI tools?',
    helpText: 'Without a policy, staff improvise with public tools.',
    options: [
      { text: 'Yes—a clear AI policy that staff know and follow', risk: 0 },
      { text: 'Informal guidance, nothing written', risk: 2, gap: 'Verbal guidance doesn’t hold. Publish an AI acceptable-use policy covering approved tools and what data may be used.' },
      { text: 'No AI policy / not sure', risk: 3, gap: 'With no AI policy, staff paste sensitive data into public tools. A clear policy is the first control.' },
    ],
  },
  {
    id: 'shadow_ai',
    text: 'Do you know which AI tools employees are actually using?',
    helpText: 'Shadow AI is where data leaks.',
    options: [
      { text: 'Yes—we have visibility into AI tool usage', risk: 0 },
      { text: 'We know the main ones, not the rest', risk: 2, gap: 'Shadow AI (unsanctioned tools) is where data leaks. Discover and inventory all AI tools in use.' },
      { text: 'No visibility / not sure', risk: 3, gap: 'Unmonitored shadow AI is the biggest exposure here. Gain visibility into what tools staff use and what data they feed them.' },
    ],
  },
  {
    id: 'sensitive_data',
    text: 'Is sensitive data (PII, source code, client info) being pasted into AI tools?',
    helpText: 'Prompt data can be retained or used for training.',
    options: [
      { text: 'No—controls prevent sensitive data in prompts', risk: 0 },
      { text: 'Probably some, we’re not sure', risk: 2, gap: 'Sensitive data in prompts can be retained or used for training. Add controls/DLP to keep regulated data out of AI tools.' },
      { text: 'Yes / very likely / not sure', risk: 3, gap: 'Pasting PII, code, or client data into public AI tools risks permanent exposure. Put guardrails and monitoring in place now.' },
    ],
  },
  {
    id: 'sanctioned',
    text: 'Do you provide approved, enterprise AI tools with data protections?',
    helpText: 'If you don’t offer safe AI, staff use unsafe AI.',
    options: [
      { text: 'Yes—staff use enterprise AI with no-training guarantees', risk: 0 },
      { text: 'Some teams, ad hoc', risk: 1, gap: 'Without a sanctioned enterprise option, staff default to consumer tools. Provide approved AI with contractual data protections.' },
      { text: 'No approved tools / not sure', risk: 2, gap: 'If you don’t offer safe AI, staff use unsafe AI. Roll out enterprise tools with data-retention/training protections.' },
    ],
  },
  {
    id: 'copilot_permissions',
    text: 'If you use Copilot / M365 AI, have you reviewed permission sprawl?',
    helpText: 'Copilot surfaces everything a user can technically access.',
    options: [
      { text: 'Yes—oversharing was remediated before rollout', risk: 0 },
      { text: 'Rolled out without a permissions review', risk: 3, gap: 'Copilot surfaces everything a user can access. Without a permissions cleanup it exposes overshared files org-wide.' },
      { text: 'Not using it yet / not sure', risk: 1, gap: 'Before enabling Copilot, remediate oversharing—otherwise AI makes existing permission sprawl instantly searchable.' },
    ],
  },
  {
    id: 'monitoring',
    text: 'Can you detect and respond to risky AI usage?',
    helpText: 'Logs without alerting miss risky usage in real time.',
    options: [
      { text: 'Yes—AI usage is monitored with alerts', risk: 0 },
      { text: 'Limited logging, no alerting', risk: 1, gap: 'Logs without alerting miss risky usage. Add monitoring that flags sensitive data going to AI tools.' },
      { text: 'No monitoring / not sure', risk: 2, gap: 'Without monitoring, AI data leaks go undetected. Add visibility and alerting on AI tool usage.' },
    ],
  },
];

type Status = 'intro' | 'assessment' | 'results';

interface Answer {
  questionId: string;
  optionIndex: number;
}

function levelFor(percent: number): {
  level: 'High' | 'Medium' | 'Low';
  color: string;
  bg: string;
  blurb: string;
} {
  if (percent >= 55) {
    return {
      level: 'High',
      color: 'text-red-700 dark:text-red-300',
      bg: 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800',
      blurb:
        'Your AI usage has serious exposure—the kind that leaks IP and regulated data through everyday prompts. An AI security audit to inventory tools, set policy, and lock down Copilot oversharing should be an immediate priority.',
    };
  }
  if (percent >= 25) {
    return {
      level: 'Medium',
      color: 'text-amber-700 dark:text-amber-300',
      bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800',
      blurb:
        'You have some controls in place but meaningful gaps remain. Closing them—especially shadow-AI visibility and keeping sensitive data out of prompts—will materially lower your exposure.',
    };
  }
  return {
    level: 'Low',
    color: 'text-green-700 dark:text-green-300',
    bg: 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800',
    blurb:
      'Your AI posture is strong. An audit keeps it that way—maintaining policy, monitoring shadow AI, and re-checking Copilot permissions as you adopt more tools.',
  };
}

export default function RiskCheckClient() {
  const [status, setStatus] = useState<Status>('intro');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [userCount, setUserCount] = useState<number>(25);

  const handleAnswer = (optionIndex: number) => {
    const question = QUESTIONS[currentIndex];
    const newAnswer: Answer = { questionId: question.id, optionIndex };
    const newAnswers = [...answers.filter((a) => a.questionId !== question.id), newAnswer];
    setAnswers(newAnswers);

    if (currentIndex < QUESTIONS.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setStatus('results');
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleRestart = () => {
    setAnswers([]);
    setCurrentIndex(0);
    setStatus('intro');
  };

  const result = useMemo(() => {
    if (answers.length !== QUESTIONS.length) return null;
    let total = 0;
    const maxPerQuestion = 3;
    const gaps: string[] = [];
    QUESTIONS.forEach((q) => {
      const a = answers.find((ans) => ans.questionId === q.id);
      if (!a) return;
      const option = q.options[a.optionIndex];
      total += option.risk;
      if (option.gap) gaps.push(option.gap);
    });
    const max = QUESTIONS.length * maxPerQuestion;
    const percent = Math.round((total / max) * 100);
    return { percent, gaps, ...levelFor(percent) };
  }, [answers]);

  const resultsSummary = useMemo(() => {
    if (!result) return '';
    const answerLines = QUESTIONS.map((q) => {
      const a = answers.find((ans) => ans.questionId === q.id);
      const chosen = a ? q.options[a.optionIndex].text : 'N/A';
      return `  • ${q.text} → ${chosen}`;
    }).join('\n');

    const gapLines = result.gaps.length
      ? result.gaps.map((g, i) => `${i + 1}. ${g}`).join('\n')
      : 'No major gaps identified—maintain current controls.';

    return `AI & SHADOW-AI SECURITY RISK CHECK RESULTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
AI Exposure Risk: ${result.level} (${result.percent}% risk score)
Approx. employees using AI tools: ${userCount}

${result.blurb}

ANSWERS:
${answerLines}

TOP GAPS TO CLOSE:
${gapLines}`;
  }, [result, answers, userCount]);

  // Intro
  if (status === 'intro') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🤖</div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">How exposed is your AI usage?</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-lg mx-auto">
              Six questions on AI policy, shadow AI, sensitive data in prompts, sanctioned tools, Copilot permission sprawl, and monitoring. Get an instant AI Exposure Risk score and your top gaps.
            </p>
          </div>

          <div className="bg-[#2813e8]/5 border border-[#2813e8]/20 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-[#2813e8] mb-2">What you&apos;ll learn:</h3>
            <ul className="text-gray-700 dark:text-gray-300 text-sm space-y-1">
              <li>• Whether your AI exposure risk is High, Medium, or Low</li>
              <li>• The specific gaps leaving you exposed</li>
              <li>• What to fix first to close the biggest holes</li>
            </ul>
          </div>

          <button
            onClick={() => setStatus('assessment')}
            className="w-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] hover:opacity-90 text-white font-semibold py-3 px-6 rounded-lg transition-opacity"
          >
            Start the Risk Check
          </button>
        </div>
      </div>
    );
  }

  // Assessment
  if (status === 'assessment') {
    const question = QUESTIONS[currentIndex];
    const progress = ((currentIndex + 1) / QUESTIONS.length) * 100;
    const currentAnswer = answers.find((a) => a.questionId === question.id);

    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span>AI Security Risk Check</span>
              <span>Question {currentIndex + 1} of {QUESTIONS.length}</span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">{question.text}</h2>
            {question.helpText && <p className="text-gray-600 dark:text-gray-400 text-sm">{question.helpText}</p>}
          </div>

          <div className="space-y-3 mb-6">
            {question.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswer(idx)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  currentAnswer?.optionIndex === idx
                    ? 'border-[#2813e8] bg-[#2813e8]/5'
                    : 'border-gray-200 dark:border-gray-700 hover:border-[#2813e8]/40 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <span className="text-gray-900 dark:text-gray-100">{option.text}</span>
              </button>
            ))}
          </div>

          <div className="flex justify-between">
            <button
              onClick={handleBack}
              disabled={currentIndex === 0}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Back
            </button>
            <button
              onClick={handleRestart}
              className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Start Over
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Results
  if (status === 'results' && result) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Score */}
        <div className={`rounded-2xl border-2 p-8 ${result.bg}`}>
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">{result.level === 'High' ? '🚨' : result.level === 'Medium' ? '⚠️' : '✅'}</div>
            <h2 className={`text-3xl font-bold mb-2 ${result.color}`}>AI Exposure Risk: {result.level}</h2>
            <p className="text-gray-700 dark:text-gray-300 max-w-xl mx-auto">{result.blurb}</p>
          </div>
          <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-4 text-center">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Risk score</div>
            <div className={`text-3xl font-bold ${result.color}`}>{result.percent}%</div>
          </div>
        </div>

        {/* Gaps */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
            {result.gaps.length ? 'Top Gaps to Close' : 'No Major Gaps Identified'}
          </h3>
          {result.gaps.length ? (
            <ul className="space-y-3">
              {result.gaps.map((gap, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#2813e8]/10 text-xs font-bold text-[#2813e8]">{i + 1}</span>
                  <span className="text-gray-700 dark:text-gray-300 leading-relaxed">{gap}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">Your controls look solid. An audit keeps policy, shadow-AI visibility, and Copilot permissions tight as you adopt more tools.</p>
          )}
        </div>

        {/* User count */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            How many employees use AI tools? <span className="text-[#2813e8] font-bold">{userCount}</span>
          </label>
          <input
            type="range"
            min="1"
            max="500"
            step="1"
            value={userCount}
            onChange={(e) => setUserCount(Number(e.target.value))}
            aria-label="Number of employees using AI tools"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#2813e8]"
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>1</span>
            <span>250</span>
            <span>500</span>
          </div>
        </div>

        {/* Offer CTA */}
        <div className="rounded-2xl border-2 border-[#2813e8]/30 bg-[#2813e8]/5 p-6 text-center">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Want these gaps closed for you?</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Inventive HQ runs an AI Security Audit that inventories shadow AI, sets policy, and remediates Copilot oversharing before it leaks your data.
          </p>
          <Link
            href="/services/ai-security-audit"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] px-8 py-3 font-semibold text-white shadow-lg transition-all hover:scale-105"
          >
            See the AI Security Audit
          </Link>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleRestart}
            className="flex-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium py-3 px-6 rounded-lg transition-colors"
          >
            🔄 Retake Check
          </button>
        </div>

        {/* Lead capture */}
        <div className="mt-8">
          <LeadCaptureForm
            source="ai-security"
            title="Get Your AI Exposure Reviewed by an Expert"
            description="Our team will review your results and send a prioritized AI security and shadow-AI remediation plan."
            deliverable="a prioritized AI security and shadow-AI remediation plan"
            resultsSummary={resultsSummary}
          />
        </div>
      </div>
    );
  }

  return null;
}
